import React, { useState, useRef, useEffect, useContext, createContext, useMemo, useId, useCallback, useImperativeHandle } from 'react';
import clsx from 'clsx';
import Tooltip from "../Tooltip/Tooltip"; // adicione este import
import { z } from "zod";

export interface ButtonI18nContext {
  getDefaultErrorMessage: () => string;
  getDefaultLoadingMessage?: () => string;
}
export const ButtonMessagesContext = createContext<ButtonI18nContext>({
  getDefaultErrorMessage: () => "Ocorreu um erro.",
  getDefaultLoadingMessage: () => "Processando...",
});

type ButtonAppearanceProps = {
  theme?: 'light' | 'dark';
  icon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  className?: string;
  buttonClassName?: string;
  size?: 'tiny' | 'small' | 'medium' | 'large'; // Adicione 'tiny' aqui
  fullWidth?: boolean;
  tooltip?: string;
};

type ButtonBehaviorProps = {
  loading?: boolean;
  disabled?: boolean;
  error?: boolean;
  errorMessage?: React.ReactNode;
  loadingMessage?: React.ReactNode;
  // Permitir ambos os tipos de evento
  onClick: (e?: React.MouseEvent<HTMLElement, MouseEvent>) => Promise<void> | void;
  tabIndex?: number;
  type?: "button" | "submit" | "reset";
};

type ButtonA11yProps = {
  ariaLabel?: string;
  ariaErrorMessage?: string;
  i18nMessages?: ButtonI18nContext;
};

type ButtonTestProps = {
  "data-testid"?: string;
  containerProps?: React.HTMLAttributes<HTMLDivElement>;
  buttonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>;
  anchorProps?: React.AnchorHTMLAttributes<HTMLAnchorElement> & AnchorTestProps; // Updated
};

type AnchorTestProps = {
  "data-testid"?: string;
  [key: string]: unknown; // Allow any extra props for testing, but avoid 'any'
};

type ButtonVariant = 'contained' | 'outlined' | 'text' | 'fab' | 'icon';
type ButtonShape = 'round' | 'square' | 'pill' | 'default' | 'circle'; // Adicione 'circle'

export interface ButtonProps
  extends ButtonAppearanceProps,
    ButtonBehaviorProps,
    ButtonA11yProps,
    ButtonTestProps {
  title?: React.ReactNode;
  children?: React.ReactNode;
  as?: 'button' | 'a';
  href?: string;
  rel?: string;
  role?: string;
  id?: string;
  onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>;
  variant?: ButtonVariant;
  shape?: ButtonShape;
  color?: string;
  shadow?: string;
  sx?: React.CSSProperties;
  target?: string; // <-- Adicione esta linha
  style?: React.CSSProperties; // <-- Adicione esta linha
  transparent?: boolean; // Novo
  noBorder?: boolean;    // Novo
  noHover?: boolean;     // <-- Adicione esta linha
  noFocus?: boolean;     // <-- Adicione esta linha
}

// Spinner extraído
const Spinner: React.FC = () => (
  <span
    className="inline-block w-5 h-5 border-2 border-t-2 border-gray-300 border-t-current rounded-full animate-spin"
    aria-hidden="true"
  ></span>
);

// Função utilitária para resolver mensagens
function resolveMessage({
  explicit,
  fallback,
  context,
}: {
  explicit?: React.ReactNode;
  fallback?: string;
  context: ButtonI18nContext;
}) {
  if (explicit !== undefined) return explicit;
  if (fallback !== undefined) return fallback;
  return context.getDefaultErrorMessage();
}


function slugify(text: string) {
  return text.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
}

// 1. Defina overloads para o componente
type ButtonPropsAnchor = ButtonProps & {
  as: 'a';
  href: string;
  ref?: React.Ref<HTMLAnchorElement>;
};
type ButtonPropsButton = ButtonProps & {
  as?: 'button';
  href?: undefined;
  ref?: React.Ref<HTMLButtonElement>;
};
type ButtonPropsOverload = ButtonPropsAnchor | ButtonPropsButton;

// 2. Use overloads na declaração do forwardRef
function ButtonComponent(
  props: ButtonPropsAnchor,
  ref: React.Ref<HTMLAnchorElement>
): React.ReactElement | null;
function ButtonComponent(
  props: ButtonPropsButton,
  ref: React.Ref<HTMLButtonElement>
): React.ReactElement | null;
function ButtonComponent(
  props: ButtonPropsOverload,
  ref: React.Ref<HTMLButtonElement | HTMLAnchorElement>
) {
  // Validação Zod em tempo de execução (apenas em dev)
  // Defina um schema básico para ButtonProps usando zod
  const ButtonPropsSchema = z.object({
    title: z.any().optional(),
    children: z.any().optional(),
    onClick: z.function().args(z.any().optional()).returns(z.any()),
    theme: z.enum(['light', 'dark']).optional(),
    icon: z.any().optional(),
    rightIcon: z.any().optional(),
    className: z.string().optional(),
    buttonClassName: z.string().optional(),
    size: z.enum(['tiny', 'small', 'medium', 'large']).optional(),
    fullWidth: z.boolean().optional(),
    tooltip: z.string().optional(),
    loading: z.boolean().optional(),
    disabled: z.boolean().optional(),
    error: z.boolean().optional(),
    errorMessage: z.any().optional(),
    loadingMessage: z.any().optional(),
    tabIndex: z.number().optional(),
    type: z.enum(['button', 'submit', 'reset']).optional(),
    ariaLabel: z.string().optional(),
    ariaErrorMessage: z.string().optional(),
    i18nMessages: z.any().optional(),
    containerProps: z.any().optional(),
    buttonProps: z.any().optional(),
    anchorProps: z.any().optional(),
    as: z.enum(['button', 'a']).optional(),
    href: z.string().optional(),
    rel: z.string().optional(),
    role: z.string().optional(),
    variant: z.enum(['contained', 'outlined', 'text', 'fab', 'icon']).optional(),
    shape: z.enum(['round', 'square', 'pill', 'default']).optional(),
    color: z.string().optional(),
    shadow: z.string().optional(),
    sx: z.any().optional(),
    "data-testid": z.string().optional(),
    onKeyDown: z.any().optional(),
    id: z.string().optional(),
  }).superRefine((val, ctx) => {
    // Se as === 'a', href é obrigatório
    if (val.as === 'a' && !val.href) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Prop 'href' é obrigatória quando 'as' é 'a'.",
        path: ['href'],
      });
    }
    // Se as === 'button', href não deve existir
    if ((val.as === 'button' || !val.as) && val.href) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Prop 'href' não deve ser usada quando 'as' é 'button'.",
        path: ['href'],
      });
    }
  });

  if (process.env.NODE_ENV !== "production") {
    const result = ButtonPropsSchema.safeParse(props);
    if (!result.success) {
      // eslint-disable-next-line no-console
      console.error("Button props inválidas:", result.error.format());
    }
  }

  const {
    title,
    children,
    onClick,
    loading = false,
    disabled = false,
    error = false,
    errorMessage,
    theme = 'light',
    icon,
    rightIcon,
    className,
    buttonClassName,
    type = "button",
    tabIndex,
    ariaLabel,
    ariaErrorMessage,
    i18nMessages,
    containerProps,
    buttonProps,
    "data-testid": dataTestId,
    tooltip, // Novo
    as, // Novo
    href, // Novo
    rel, // Novo
    fullWidth, // Added
    size, // Added
    anchorProps, // Novo
    role, // Novo
    variant, // <-- Add this line
    shape, // <-- Add this line to fix the error
    color, // <-- Add this line to fix the error
    shadow, // <-- Also destructure shadow for dynamicStyle
    sx, // <-- Also destructure sx for dynamicStyle
    target, // <-- Add this line to fix the error
    style, // <-- Adicione esta linha
    transparent = false, // Novo
    noBorder = false,    // Novo
    noHover = false,     // <-- Add this line
    noFocus = false,     // <-- Add this line
  } = props;

  // Forçar shape circle para variant fab
  const effectiveShape = variant === "fab" ? "circle" : shape;

  const isControlledLoading = loading !== undefined;
  const isControlledError = error !== undefined;

  const [internalLoading, setInternalLoading] = useState(false);
  const [internalError, setInternalError] = useState(false);

  const isLoading = isControlledLoading ? loading : internalLoading;
  const hasError = isControlledError ? error : internalError;
  const contextI18n = useContext(ButtonMessagesContext);
  const i18n = i18nMessages || contextI18n;
  const isMounted = useRef(true);
  // Troque useRef por useId para SSR-friendly
  const uniqueErrorId = useId();

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  // Troque handleClick por useCallback para memoizar a função
  const handleClick = useCallback(
    async (e?: React.MouseEvent<HTMLElement, MouseEvent>) => {
      if (disabled || isLoading) return;
      if (!isControlledLoading) setInternalLoading(true);
      if (!isControlledError) setInternalError(false);
      try {
        await onClick(e);
      } catch {
        if (!isControlledError) setInternalError(true);
      } finally {
        if (isMounted.current && !isControlledLoading) setInternalLoading(false);
      }
    },
    [
      disabled,
      isLoading,
      isControlledLoading,
      isControlledError,
      onClick,
    ]
  );

  // Memoize resolvedErrorMessage
  const resolvedErrorMessage = useMemo(() =>
    resolveMessage({
      explicit: errorMessage,
      fallback: ariaErrorMessage,
      context: i18n,
    }),
    [errorMessage, ariaErrorMessage, i18n]
  );

  // resolvedLoadingMessage removed because it was unused

  const content = children ?? title;

  const baseTestId =
    dataTestId ||
    (typeof (content) === 'string' ? `button-${slugify(content as string)}` : 'button');
  const resolvedContainerTestId = `${baseTestId}-container`;
  const resolvedButtonTestId =
    buttonProps?.['data-testid' as keyof typeof buttonProps] ??
    baseTestId;

  const isSafeHref = (url?: string) =>
    !url || /^(https?:|\/|#)/i.test(url);

  const safeHref = isSafeHref(href) ? href : undefined;

  // Prioriza tooltip > anchorProps.title/buttonProps.title > undefined
  const resolvedTitle =
    tooltip ??
    (as === 'a' || href
      ? anchorProps?.title
      : buttonProps?.title);

  const isTiny = size === 'tiny';
  const tinyIcon = icon || rightIcon; // Prioriza icon, senão rightIcon

  const buttonClass = clsx(
    // Base
    "inline-flex items-center justify-center font-bold transition select-none gap-2",
    // Tamanho
    {
      "px-3 py-1.5 text-sm min-w-[80px] min-h-[32px]": size === "small" && effectiveShape !== "circle",
      "px-5 py-3 text-base min-w-[120px] min-h-[44px]": (!size || size === "medium") && effectiveShape !== "circle",
      "px-8 py-4 text-lg min-w-[160px] min-h-[56px]": size === "large" && effectiveShape !== "circle",
      "w-8 h-8 p-0 text-base justify-center": size === "tiny" && effectiveShape !== "circle",
      "w-full": fullWidth && effectiveShape !== "circle",

      // Shape circle: largura e altura iguais, sem padding, centralizado
      "w-12 h-12 p-0 justify-center aspect-square": effectiveShape === "circle" && !size,
      "w-8 h-8 p-0 justify-center aspect-square": effectiveShape === "circle" && size === "small",
      "w-10 h-10 p-0 justify-center aspect-square": effectiveShape === "circle" && size === "medium",
      "w-14 h-14 p-0 justify-center aspect-square": effectiveShape === "circle" && size === "large",
    },
    // Tema (remova todas as bordas se noBorder)
    noBorder
      ? "border-0 !border-none"
      : [
          theme === "light" && !disabled && !error && !transparent
            ? [
                "bg-white text-gray-900 border border-gray-300 focus:ring-purple-300",
                !noHover && "hover:bg-gray-100 hover:border-gray-400"
              ]
            : "",
          theme === "dark" && !disabled && !error && !transparent
            ? [
                "bg-gray-800 text-white border border-gray-700 focus:ring-purple-700",
                !noHover && "hover:bg-gray-700 hover:border-gray-500"
              ]
            : "",
          error && !transparent
            ? "border-red-500 bg-red-50 text-red-700"
            : "",
        ],
    {
      "opacity-50 cursor-not-allowed pointer-events-none": disabled || isLoading,
      "bg-transparent": transparent, // Novo
      // Remova hover:shadow-lg se noHover
      ...(!noHover && { "hover:shadow-lg focus:shadow-lg": true }),
      // Focus visual só se !noFocus
      ...( !noFocus
        ? [
            "focus:outline-none",
            "focus:ring-2",
            "focus:ring-primary-500",
            "focus-visible:outline-2",
            "focus-visible:outline-primary-500",
            "focus-visible:outline-offset-2"
          ]
        : []
      ),
    },
    // Shape
    {
      "rounded-full": effectiveShape === "circle",
      "rounded-3xl": effectiveShape === "round",
      "rounded-lg": effectiveShape === "default",
      "rounded-xl": effectiveShape === "pill",
      "rounded-none": effectiveShape === "square",
    },
    "active:scale-95",
    "transition-all duration-150",
    buttonClassName // FabMenu injeta tudo aqui!
  );

  const dynamicStyle: React.CSSProperties = {
    ...(color ? { backgroundColor: color } : {}),
    ...(shadow ? { boxShadow: shadow } : {}),
    ...sx,
    ...style, // <-- Mescle style aqui
  };

  const buttonInnerRef = useRef<HTMLButtonElement>(null);
  const anchorInnerRef = useRef<HTMLAnchorElement>(null);

  // Exponha métodos imperativos via ref
  useImperativeHandle(ref, () => {
    if (as === 'a' || href) {
      if (anchorInnerRef.current) return anchorInnerRef.current;
    } else {
      if (buttonInnerRef.current) return buttonInnerRef.current;
    }
    // Fallback to satisfy the type (should never happen in practice)
    return {} as HTMLButtonElement & HTMLAnchorElement;
  }, [as, href]);

  const fallbackAriaLabel =
    typeof ariaLabel === 'string' && ariaLabel
      ? ariaLabel
      : typeof resolvedTitle === 'string' && resolvedTitle
      ? resolvedTitle
      : typeof content === 'string' && content
      ? content
      : variant === "fab" && React.isValidElement(icon) && (icon.type as { displayName?: string })?.displayName
      ? (icon.type as { displayName?: string }).displayName
      : "Ação";

  const buttonElement = (
    <div
      {...containerProps}
      className={clsx(
        "flex flex-col items-center w-full relative",
        { "w-full": fullWidth },
        theme,
        { error: hasError },
        className
      )}
      style={style} // <-- Add this line
      data-testid={resolvedContainerTestId}
      role={role ?? containerProps?.role ?? "group"}
    >
      {as === 'a' || href ? (
        <a
          {...anchorProps}
          ref={anchorInnerRef}
          href={disabled || isLoading ? undefined : safeHref}
          className={clsx(buttonClass, noBorder && "group")}
          style={dynamicStyle}
          aria-disabled={disabled || isLoading}
          aria-label={fallbackAriaLabel}
          tabIndex={tabIndex}
          data-testid={resolvedButtonTestId}
          title={resolvedTitle || fallbackAriaLabel}
          role="button"
          rel={rel ?? (href && href.startsWith('http') ? 'noopener noreferrer' : undefined)}
          aria-busy={isLoading}
          target={target}
          onClick={e => {
            if (disabled || isLoading) {
              e.preventDefault();
              return;
            }
            handleClick(e);
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleClick(e as React.KeyboardEvent<HTMLAnchorElement> as unknown as React.MouseEvent<HTMLElement, MouseEvent>);
            }
          }}
          aria-haspopup="true"
        >
          {isTiny ? (
            <span className="flex items-center justify-center w-full h-full">
              {isLoading ? (
                <Spinner />
              ) : (
                tinyIcon && <span className="flex items-center">{tinyIcon}</span>
              )}
              <span className="sr-only" aria-live="polite">
                {resolvedTitle}
              </span>
            </span>
          ) : (
            <span className="flex items-center gap-2">
              {isLoading ? (
                <Spinner />
              ) : variant === "fab" && effectiveShape === "circle" ? (
                // FAB clássico: só ícone, sem texto
                (icon && (
                  <span
                    className={clsx(
                      "flex items-center",
                      noBorder && "group-hover:shadow-[0_0_8px_2px_rgba(0,0,0,0.15)] group-focus:shadow-[0_0_8px_2px_rgba(0,0,0,0.18)] transition-shadow"
                    )}
                  >
                    {icon}
                  </span>
                ))
              ) : (
                <>
                  {icon && (
                    <span
                      className={clsx(
                        "mr-2 flex items-center",
                        noBorder && "group-hover:shadow-[0_0_8px_2px_rgba(0,0,0,0.15)] group-focus:shadow-[0_0_8px_2px_rgba(0,0,0,0.18)] transition-shadow"
                      )}
                    >
                      {icon}
                    </span>
                  )}
                  <span>{content}</span>
                  {rightIcon && (
                    <span
                      className={clsx(
                        "ml-2 flex items-center",
                        noBorder && "group-hover:shadow-[0_0_8px_2px_rgba(0,0,0,0.15)] group-focus:shadow-[0_0_8px_2px_rgba(0,0,0,0.18)] transition-shadow"
                      )}
                    >
                      {rightIcon}
                    </span>
                  )}
                </>
              )}
              <span className="sr-only" aria-live="polite">
                {resolvedTitle}
              </span>
            </span>
          )}
        </a>
      ) : (
        <button
          {...buttonProps}
          ref={buttonInnerRef}
          type={type}
          className={clsx(buttonClass, noBorder && "group")}
          style={dynamicStyle}
          onClick={handleClick}
          disabled={disabled || isLoading}
          aria-disabled={disabled || isLoading}
          aria-label={fallbackAriaLabel}
          aria-busy={isLoading}
          tabIndex={tabIndex}
          data-testid={resolvedButtonTestId}
          title={resolvedTitle || fallbackAriaLabel}
        >
          {/* Conteúdo do botão */}
          {isTiny ? (
            <span className="flex items-center justify-center w-full h-full">
              {isLoading ? (
                <Spinner />
              ) : (
                tinyIcon && <span className="flex items-center">{tinyIcon}</span>
              )}
              <span className="sr-only" aria-live="polite">
                {resolvedTitle}
              </span>
            </span>
          ) : (
            <span className="flex items-center gap-1">
              {isLoading ? (
                <Spinner />
              ) : variant === "fab" && shape === "round" ? (
                (icon && <span className="flex items-center">{icon}</span>)
              ) : (
                <>
                  {icon && <span className="mr-1 flex items-center">{icon}</span>}
                  <span>{content}</span>
                  {rightIcon && <span className="ml-1 flex items-center">{rightIcon}</span>}
                </>
              )}
              <span className="sr-only" aria-live="polite">
                {resolvedTitle}
              </span>
            </span>
          )}
        </button>
      )}
      {hasError && (
        <p
          className="text-red-600 text-[0.95rem] mt-2 text-center font-medium tracking-[0.01em] leading-[1.3] px-2 break-words animate-fade-in"
          role="alert"
          aria-live="assertive"
          id={uniqueErrorId}
        >
          {resolvedErrorMessage}
        </p>
      )}
    </div>
  );

  if (tooltip) {
    return (
      <Tooltip tooltip={tooltip}>
        {buttonElement}
      </Tooltip>
    );
  }
  return buttonElement;
}

// 3. Exporte usando forwardRef e memo
const Button = React.memo(
  React.forwardRef(ButtonComponent)
) as React.MemoExoticComponent<
  React.ForwardRefExoticComponent<
    ButtonPropsOverload & React.RefAttributes<HTMLButtonElement | HTMLAnchorElement>
  >
>;

Button.displayName = "Button";

/**
 * Componente de botão reutilizável e acessível para React, com suporte avançado a temas, loading, erro, links, ícones, tamanhos e internacionalização.
 * 
 * ## Funcionalidades
 * - **TailwindCSS:** Todo o estilo é feito apenas com classes utilitárias do Tailwind, sem dependência de CSS externo.
 * - **Temas:** `light` (claro) ou `dark` (escuro)
 * - **Tamanhos:** `tiny`, `small`, `medium`, `large`
 * - **Ícones:** à esquerda (`icon`) e/ou à direita (`rightIcon`)
 * - **Variações:** `contained`, `outlined`, `text`, `fab`, `icon`
 * - **Formas:** `round`, `square`, `pill`, `default`
 * - **Full width:** opção para ocupar 100% da largura do container
 * - **Tooltip:** suporte a tooltip customizado
 * - **Loading:** estado de carregamento com spinner e mensagem customizável
 * - **Erro:** estado de erro com mensagem customizável e acessível, animado com fade-in
 * - **Acessibilidade:** suporte a `aria-label`, `aria-errormessage`, mensagens para leitores de tela, roles e foco visível
 * - **Internacionalização:** mensagens via contexto ou props (`i18nMessages`)
 * - **Links:** pode renderizar como `<button>` ou `<a>` (link seguro)
 * - **Desabilitado:** suporte a estado desabilitado
 * - **Testabilidade:** `data-testid` e props extras para container, botão e link
 * - **Estilo dinâmico:** aceita cor, sombra e estilos inline via props
 * - **ForwardRef:** permite acessar o elemento DOM subjacente (`button` ou `a`)
 * - **SSR-friendly:** uso de `useId` para acessibilidade consistente em SSR
 * - **Animações:** efeitos de transição, sombra, clique e erro usando Tailwind (incluindo fade-in para mensagens de erro)
 * - **Validação condicional:** Validação runtime com Zod garante que `href` é obrigatório quando `as: 'a'` e proibido quando `as: 'button'`.
 * 
 * ## Props
 * @prop {React.ReactNode} [title] - Texto ou elemento principal do botão (usado como fallback se `children` não for fornecido).
 * @prop {React.ReactNode} [children] - Conteúdo do botão (tem prioridade sobre `title`).
 * @prop {(e?: React.MouseEvent<HTMLElement, MouseEvent>) => Promise<void> | void} onClick - Função chamada ao clicar, recebe o evento.
 * @prop {'light' | 'dark'} [theme='light'] - Tema visual do botão.
 * @prop {React.ReactNode} [icon] - Ícone à esquerda do texto.
 * @prop {React.ReactNode} [rightIcon] - Ícone à direita do texto.
 * @prop {string} [className] - Classe extra para o container externo.
 * @prop {string} [buttonClassName] - Classe extra para o botão interno.
 * @prop {'tiny' | 'small' | 'medium' | 'large'} [size] - Tamanho do botão.
 * @prop {boolean} [fullWidth] - Ocupa 100% da largura do container.
 * @prop {string} [tooltip] - Texto de tooltip (atributo `title` ou componente Tooltip).
 * @prop {boolean} [loading] - Estado de carregamento controlado (spinner e mensagem).
 * @prop {boolean} [disabled] - Desabilita o botão.
 * @prop {boolean} [error] - Estado de erro controlado.
 * @prop {React.ReactNode} [errorMessage] - Mensagem de erro customizada.
 * @prop {React.ReactNode} [loadingMessage] - Mensagem de carregamento customizada.
 * @prop {number} [tabIndex] - Índice de tabulação.
 * @prop {"button" | "submit" | "reset"} [type="button"] - Tipo do botão (apenas para `<button>`).
 * @prop {string} [ariaLabel] - Rótulo acessível para leitores de tela.
 * @prop {string} [ariaErrorMessage] - Mensagem de erro acessível.
 * @prop {ButtonI18nContext} [i18nMessages] - Mensagens customizadas para internacionalização.
 * @prop {string} [data-testid] - Test ID para o container.
 * @prop {React.HTMLAttributes<HTMLDivElement>} [containerProps] - Props extras para o container `<div>`.
 * @prop {React.ButtonHTMLAttributes<HTMLButtonElement>} [buttonProps] - Props extras para o `<button>`.
 * @prop {React.AnchorHTMLAttributes<HTMLAnchorElement>} [anchorProps] - Props extras para o `<a>`.
 * @prop {'button' | 'a'} [as] - Força renderização como `<button>` ou `<a>`.
 * @prop {string} [href] - URL do link (ativa `<a>`, obrigatório se `as="a"`).
 * @prop {string} [rel] - Atributo rel do link (por padrão, `noopener noreferrer` para links externos).
 * @prop {string} [role] - Sobrescreve o papel do container para acessibilidade.
 * @prop {'contained' | 'outlined' | 'text' | 'fab' | 'icon'} [variant] - Variação visual do botão.
 * @prop {'round' | 'square' | 'pill' | 'default'} [shape] - Formato do botão.
 * @prop {string} [color] - Cor de fundo customizada.
 * @prop {string} [shadow] - Sombra customizada.
 * @prop {React.CSSProperties} [sx] - Estilos inline adicionais.
 * @prop {React.KeyboardEventHandler<HTMLButtonElement>} [onKeyDown] - Manipulador de evento de teclado.
 * 
 * ## Acessibilidade
 * - Usa `aria-label`, `aria-disabled`, `aria-busy`, `aria-live` e `role` para acessibilidade aprimorada.
 * - Mensagens de erro e loading são lidas por leitores de tela.
 * - Foco visível com outline e sombra para navegação por teclado.
 * 
 * ## Segurança
 * - Só permite hrefs seguros (`http(s):`, `/`, `#`).
 * - Links externos recebem `rel="noopener noreferrer"` automaticamente.
 * - Validação condicional em runtime: se `as="a"`, `href` é obrigatório; se `as="button"`, `href` não deve ser usado.
 * 
 * ## Testes
 * - Use `data-testid` para selecionar o botão e o container em testes.
 * - Props extras permitem customização para cenários de teste.
 * 
 * ## Exemplo de uso
 * ```tsx
 * <Button title="Salvar" onClick={handleSave} />
 * <Button as="a" href="https://exemplo.com" target="_blank" anchorProps={{ download: true }}>Baixar</Button>
 * <Button icon={<SaveIcon />} size="large" loading>Salvando...</Button>
 * <Button variant="fab" icon={<AddIcon />} color="#1976d2" shadow="0 4px 12px rgba(0,0,0,0.2)" />
 * ```
 * 
 * @remarks
 * - O tipo do ref depende da prop `as`/`href`: será `HTMLButtonElement` para `<button>` e `HTMLAnchorElement` para `<a>`.
 * - ⚠️ Se usar `as="a"` ou `href`, o botão **não submete formulários** mesmo com `type="submit"`. Use `<button type="submit">` para esse caso.
 * - O botão é totalmente controlável ou pode ser usado de forma não controlada (loading/erro internos).
 * - Para internacionalização, forneça um contexto customizado via `ButtonMessagesContext.Provider`.
 * - Validação condicional de props em runtime
 */
export default Button;

